import type RAPIER from "@dimforge/rapier3d-compat";
import { StateHasher } from "@rpg/kernel";
import { fromBase64, toBase64 } from "./base64";
import { createLayers, type LayerTable, type Layers } from "./layers";
import { WaterVolumes, type WaterAt, type WaterVolume } from "./water";
import { applyLimits, checkJointSpec, jointData, revoluteAngle, sphericalAngles, type JointAngles, type JointId, type JointSpec } from "./joints";

// The collision world: one small interface over a Rapier world, stepped by the game inside its fixed sim tick
// (decision 0042: the sim writes kinematic targets, steps, reads results back).
//
// - Bodies carry stable game IDs (strings the game picks), never Rapier handles: every call and every query result
//   speaks IDs, and the ID ↔ handle map travels in the snapshot, so a restored world answers to the same IDs.
// - Colliders sit on named layers (layers.ts) and may carry a surface tag ("grass", "water"), which queries return.
// - Snapshot and restore round-trip the whole Rapier world (its own `takeSnapshot`, base64 in JSON) with the maps.
// - `hash()` covers every body's kind, position, rotation, velocities and sleep state, in ID order, bit-exact.
// - Queries (rays, shape casts, overlaps) see the world as of the last `step()`: a body added or teleported since is
//   found after the next step (Rapier updates its broad phase in the step). A restored world is queryable at once.
//
// Determinism: the standard Rapier build repeats exactly on one device given the same calls in the same order
// (0042). Rapier's world is not the whole sim: the game's own state (a controller's intent, timers) lives in the
// kernel and is saved there.
//
// - Characters (A2): a kinematic body with Rapier's kinematic character controller (slopes, autostep, snap to ground,
//   sliding along walls). The game asks to move it by a translation each step; the world works out how far it can go,
//   sets the body's kinematic target, and answers grounded or not and what it touched (with surface tags). Rapier's
//   controllers aren't part of its snapshot, so the world keeps each character's settings and rebuilds the controller
//   after a restore.
// - Water volumes (A2, water.ts): boxes with a surface height, answered by a point query.
// - Joints (B5, joints.ts): spherical and revolute joints between bodies, with limits, under stable game IDs that
//   travel in the snapshot like the bodies' (Rapier's own snapshot keeps the joints and their limits). Removing a
//   body removes its joints.
// - Sensors (B, combat): a collider can be a sensor (`ColliderSpec.sensor`): hurt volumes ride on a fighter's body,
//   never block anything, and are found by `sweep`, a hit volume's shape overlapped at sub-samples along its path on
//   the fixed step. The hit rules (when a move can hit, one contact per target) stay in @rpg/combat (decision 0042).
//
// `rapier` is the escape hatch for debug drawing; gameplay code goes through the methods here.

/** The Rapier module, once `initCollision` has resolved. */
export type Rapier = typeof RAPIER;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** A body's stable game ID. */
export type BodyId = string;

/** fixed: never moves (the world); dynamic: simulated; kinematic: moved by the game (`moveKinematic`), pushes dynamics. */
export type BodyKind = "fixed" | "dynamic" | "kinematic";

/** A convex primitive, usable both as a collider and as a cast or overlap shape. */
export type PrimitiveShape =
  | { type: "cuboid"; halfExtents: Vec3 }
  | { type: "ball"; radius: number }
  /** Along local y: the segment's half length plus a radius. */
  | { type: "capsule"; halfHeight: number; radius: number }
  /** Along local y. */
  | { type: "cylinder"; halfHeight: number; radius: number };

/** Any collider shape. */
export type ShapeSpec =
  | PrimitiveShape
  /**
   * A height grid centred on the body: `rows` cells along z and `cols` cells along x, so (rows + 1) × (cols + 1)
   * samples, row-major from -z: `heights[iz * (cols + 1) + ix]`. `size` is the whole field's extent in x and z, and
   * `size.y` scales the samples.
   */
  | { type: "heightfield"; rows: number; cols: number; heights: ArrayLike<number>; size: Vec3 }
  /** A triangle mesh: xyz per vertex, three indices per triangle. For static world geometry. */
  | { type: "trimesh"; vertices: ArrayLike<number>; indices: ArrayLike<number> }
  /** The convex hull of points (xyz per point, about the body): a breakable's fragment, a rock. */
  | { type: "convex"; points: ArrayLike<number> };

export interface ColliderSpec {
  shape: ShapeSpec;
  /** A layer from the world's table. */
  layer: string;
  /** Offset and rotation from the body. */
  offset?: Vec3;
  rotation?: Quat;
  /** Mass from density (dynamic bodies). Default 1. */
  density?: number;
  friction?: number;
  restitution?: number;
  /** A surface tag queries return ("grass", "stone", "water"). */
  tag?: string;
  /** A sensor: it never pushes or blocks anything (characters, dynamics), and queries see it only when they ask for
   *  `sensors` (a hurt volume found by a hit volume's sweep). */
  sensor?: boolean;
  /** Report contacts pushing on this collider harder than this force (N) after each step (`impacts()`): a breakable
   *  struck by a falling or thrown body. Contacts between two non-dynamic bodies (a character walking into it) have
   *  no force and never report. */
  impactThreshold?: number;
}

/** A contact that pushed harder than a collider's `impactThreshold` during the last step. */
export interface Impact {
  /** The bodies in contact (the one whose collider asked first). */
  a: BodyId;
  b: BodyId;
  /** The contact's total force over the step (N). */
  force: number;
}

export interface BodySpec {
  id: BodyId;
  kind: BodyKind;
  position?: Vec3;
  rotation?: Quat;
  colliders: readonly ColliderSpec[];
  linearDamping?: number;
  angularDamping?: number;
  /** Continuous collision detection for small fast bodies. */
  ccd?: boolean;
  /** Default true. */
  canSleep?: boolean;
}

export interface CollisionWorldOptions {
  /** The fixed step, seconds (the runtime's `clock.dt`). Every `step()` advances this much. */
  dt: number;
  /** Layer name → the layers it collides with. */
  layers: LayerTable;
  /** Default (0, -9.81, 0). */
  gravity?: Vec3;
  /** Rapier's solver iterations per step (default Rapier's, 4). More hold joint limits and stacks stiffer. */
  solverIterations?: number;
}

export interface QueryOptions {
  /** Only colliders on these layers. Default every layer. */
  layers?: readonly string[];
  /** Skip this body's colliders (the caster itself). */
  exclude?: BodyId;
  /** Include sensor colliders. Default false. */
  sensors?: boolean;
  /** Only bodies this returns true for (a camera skipping occluders it has looked past, a probe limited to one body). */
  filter?: (id: BodyId) => boolean;
}

/** What a ray or shape cast hit. */
export interface Hit {
  id: BodyId;
  tag: string | null;
  /** Metres along the (normalised) direction. */
  distance: number;
  /** The contact point on the body hit, world space. */
  point: Vec3;
  /** The surface normal of the body hit at that point, world space. */
  normal: Vec3;
}

/** One body's full motion state. */
export interface BodyState {
  id: BodyId;
  kind: BodyKind;
  position: Vec3;
  rotation: Quat;
  linvel: Vec3;
  angvel: Vec3;
  sleeping: boolean;
}

/** A collision world's saved state. JSON-safe. */
export interface CollisionSnapshot {
  format: "rpg.collision@1";
  /** Rapier's own world snapshot (`World.takeSnapshot`), base64. */
  rapier: string;
  /** [id, Rapier body handle], in the order the bodies were added. */
  bodies: [BodyId, number][];
  /** [Rapier collider handle, tag] for every tagged collider. */
  tags: [number, string][];
  /** Steps taken. */
  steps: number;
  /** The water volumes (absent in snapshots from before they existed: the world's volumes are then kept). */
  water?: WaterVolume[];
  /** [id, Rapier joint handle, spec] for every joint, in the order added (absent before joints existed: none). */
  joints?: [JointId, number, JointSpec][];
}

/** The snapshot format tag. */
export const COLLISION_SNAPSHOT_FORMAT = "rpg.collision@1" as const;

/** Planted defects, to prove a game's checks can fail. */
export interface CollisionFaults {
  /** `restore` puts back the ID map and the step count but keeps the current Rapier world (a save without the physics). */
  restoreWithoutSnapshot: boolean;
  /** Characters never snap to the ground, whatever their settings: walking down a slope or a step leaves the ground. */
  noGroundSnap: boolean;
  /** No second try when Rapier's autostep declines a step met head-on: steps taller than the capsule's lower sphere
   *  centre stall (a course's "every allowed step is climbed" check must go red). */
  noStepAssist: boolean;
  /** Water queries answer by each volume's box alone, ignoring its shape (a stream's bank reads as water). */
  waterBoxOnly: boolean;
}

/** How a character moves over the world (Rapier's kinematic character controller). Angles in radians. */
export interface CharacterSpec {
  /** The gap kept between the character's shape and the world, m (Rapier's offset). Default 0.02. */
  skin?: number;
  /** The steepest slope the character walks up. */
  maxClimbAngle: number;
  /** Slopes at least this steep slide it down. Default `maxClimbAngle`. */
  minSlideAngle?: number;
  /** Climb steps up to `maxHeight` m whose top is at least `minWidth` m deep (dynamic bodies too when
   *  `includeDynamic`); null or absent: no autostep. */
  autostep?: { maxHeight: number; minWidth: number; includeDynamic?: boolean } | null;
  /** Stay on the ground when it falls away by at most this many metres (slopes, steps down); null or absent: no snap. */
  snapToGround?: number | null;
  /** Slide along what it hits instead of stopping. Default true. */
  slide?: boolean;
  /** Push the dynamic bodies it walks into. Default true. */
  pushDynamic?: boolean;
  /** Its mass for those pushes, kg. Default: from its collider. */
  mass?: number;
}

/** Something a character touched during a move. */
export interface CharacterContact {
  id: BodyId;
  tag: string | null;
  /** The surface normal of what was touched, world space, pointing out of it. */
  normal: Vec3;
}

/** The result of one `moveCharacter`. */
export interface CharacterMove {
  /** The translation the character will make this step (after collisions, steps and snapping). */
  moved: Vec3;
  /** Whether it ends the move standing on something it can stand on. */
  grounded: boolean;
  /** What it touched, in Rapier's order, each body once. */
  contacts: CharacterContact[];
}

export class CollisionWorld {
  readonly dt: number;
  readonly layers: Layers;
  /** Planted defects (tests only). */
  readonly faults: CollisionFaults = { restoreWithoutSnapshot: false, noGroundSnap: false, noStepAssist: false, waterBoxOnly: false };
  private readonly R: Rapier;
  private readonly water = new WaterVolumes();
  private characters = new Map<BodyId, { spec: CharacterSpec; ctrl: RAPIER.KinematicCharacterController | null }>();
  private world: RAPIER.World;
  private byId = new Map<BodyId, number>();
  private byHandle = new Map<number, BodyId>();
  private tags = new Map<number, string>();
  private joints = new Map<JointId, { handle: number; spec: JointSpec }>();
  private stepCount = 0;
  private readonly solverIterations: number | undefined;
  /** Colliders asking for impacts (the step drains an event queue only while there are any). */
  private impactColliders = 0;
  private events: RAPIER.EventQueue | null = null;
  private lastImpacts: Impact[] = [];

  constructor(rapier: Rapier, o: CollisionWorldOptions) {
    if (!(o.dt > 0)) throw new Error(`collision world: dt must be > 0 (got ${o.dt})`);
    this.R = rapier;
    this.dt = o.dt;
    this.layers = createLayers(o.layers);
    this.world = new rapier.World(o.gravity ?? { x: 0, y: -9.81, z: 0 });
    this.world.timestep = o.dt;
    this.solverIterations = o.solverIterations;
    if (o.solverIterations !== undefined) this.world.numSolverIterations = o.solverIterations;
  }

  /** The Rapier world: an escape hatch for debug drawing and the parts this interface doesn't cover yet. It is
   *  replaced by `restore`, so never keep it. */
  get rapier(): RAPIER.World {
    return this.world;
  }

  /** Steps taken (restored by `restore`). */
  get steps(): number {
    return this.stepCount;
  }

  // ── bodies ───────────────────────────────────────────────────────────────────────────────────────

  /** Add a body with its colliders. Throws if the ID is taken or a layer is unknown. */
  add(spec: BodySpec): void {
    if (this.byId.has(spec.id)) throw new Error(`collision world: body "${spec.id}" already exists`);
    if (spec.colliders.length === 0) throw new Error(`collision world: body "${spec.id}" has no colliders`);
    const R = this.R;
    const desc =
      spec.kind === "dynamic" ? R.RigidBodyDesc.dynamic() : spec.kind === "kinematic" ? R.RigidBodyDesc.kinematicPositionBased() : R.RigidBodyDesc.fixed();
    const p = spec.position ?? { x: 0, y: 0, z: 0 };
    desc.setTranslation(p.x, p.y, p.z);
    if (spec.rotation) desc.setRotation(spec.rotation);
    if (spec.linearDamping !== undefined) desc.setLinearDamping(spec.linearDamping);
    if (spec.angularDamping !== undefined) desc.setAngularDamping(spec.angularDamping);
    if (spec.ccd) desc.setCcdEnabled(true);
    if (spec.canSleep === false) desc.setCanSleep(false);
    const groupsOf = spec.colliders.map((c) => this.layers.groups(c.layer)); // validates layers before creating anything
    const descs = spec.colliders.map((c) => this.colliderDesc(c.shape)); // and shapes
    const body = this.world.createRigidBody(desc);
    spec.colliders.forEach((c, i) => {
      const cd = descs[i]!;
      if (c.offset) cd.setTranslation(c.offset.x, c.offset.y, c.offset.z);
      if (c.rotation) cd.setRotation(c.rotation);
      if (c.density !== undefined) cd.setDensity(c.density);
      if (c.friction !== undefined) cd.setFriction(c.friction);
      if (c.restitution !== undefined) cd.setRestitution(c.restitution);
      if (c.sensor) cd.setSensor(true);
      if (c.impactThreshold !== undefined) {
        cd.setActiveEvents(R.ActiveEvents.CONTACT_FORCE_EVENTS);
        cd.setContactForceEventThreshold(c.impactThreshold);
        this.impactColliders++;
      }
      cd.setCollisionGroups(groupsOf[i]!);
      const col = this.world.createCollider(cd, body);
      if (c.tag !== undefined) this.tags.set(col.handle, c.tag);
    });
    this.byId.set(spec.id, body.handle);
    this.byHandle.set(body.handle, spec.id);
  }

  /** Remove a body, its colliders and its joints. Unknown IDs are ignored. */
  remove(id: BodyId): void {
    const h = this.byId.get(id);
    if (h === undefined) return;
    // Rapier removes the body's joints with it; forget their IDs
    for (const [jid, j] of this.joints) if (j.spec.a === id || j.spec.b === id) this.joints.delete(jid);
    const body = this.world.getRigidBody(h);
    for (let i = 0; i < body.numColliders(); i++) {
      const c = body.collider(i);
      this.tags.delete(c.handle);
      if (c.activeEvents() & this.R.ActiveEvents.CONTACT_FORCE_EVENTS) this.impactColliders--;
    }
    this.removeCharacter(id);
    this.world.removeRigidBody(body);
    this.byId.delete(id);
    this.byHandle.delete(h);
  }

  has(id: BodyId): boolean {
    return this.byId.has(id);
  }

  /** Every body ID, in the order added. */
  ids(): BodyId[] {
    return [...this.byId.keys()];
  }

  kind(id: BodyId): BodyKind {
    return kindOf(this.body(id));
  }

  /** Position and rotation. */
  pose(id: BodyId): { position: Vec3; rotation: Quat } {
    const b = this.body(id);
    return { position: v3(b.translation()), rotation: q4(b.rotation()) };
  }

  /** Linear and angular velocity. */
  velocity(id: BodyId): { linear: Vec3; angular: Vec3 } {
    const b = this.body(id);
    return { linear: v3(b.linvel()), angular: v3(b.angvel()) };
  }

  /** Teleport a body (zeroes nothing; wakes it). For kinematic bodies in play use `moveKinematic`. */
  setPose(id: BodyId, position: Vec3, rotation?: Quat): void {
    const b = this.body(id);
    b.setTranslation(position, true);
    if (rotation) b.setRotation(rotation, true);
    // the body's colliders move now, so a character moved next starts from here (other queries still wait for a step)
    this.world.propagateModifiedBodyPositionsToColliders();
  }

  /** Where a kinematic body should be at the end of the next step; Rapier moves it there and pushes what it meets. */
  moveKinematic(id: BodyId, position: Vec3, rotation?: Quat): void {
    const b = this.body(id);
    if (!b.isKinematic()) throw new Error(`collision world: "${id}" is not kinematic`);
    b.setNextKinematicTranslation(position);
    if (rotation) b.setNextKinematicRotation(rotation);
  }

  setVelocity(id: BodyId, linear: Vec3, angular?: Vec3): void {
    const b = this.body(id);
    b.setLinvel(linear, true);
    if (angular) b.setAngvel(angular, true);
  }

  /** Change a body's linear and angular damping (a ragdoll damped harder once it has landed). */
  /** Put a body to sleep now (its velocities zeroed; it wakes when something touches it or a call moves it). */
  sleep(id: BodyId): void {
    this.body(id).sleep();
  }

  setDamping(id: BodyId, linear: number, angular: number): void {
    const b = this.body(id);
    b.setLinearDamping(linear);
    b.setAngularDamping(angular);
  }

  /** An instant change of momentum (N·s), through the centre of mass or at a world point. Wakes the body. */
  applyImpulse(id: BodyId, impulse: Vec3, point?: Vec3): void {
    const b = this.body(id);
    if (point) b.applyImpulseAtPoint(impulse, point, true);
    else b.applyImpulse(impulse, true);
  }

  /**
   * How thick a body is as a stick (m): for each collider the second smallest of its three extents (a pole's or a
   * rail's thickness, a wall's height, a boulder's diameter), the largest over the body's colliders. A camera looks
   * past bodies thinner than some width and stops at the rest; a wall is thin one way only, so it stays solid.
   * Heightfields and triangle meshes are Infinity. Read from Rapier's colliders, so it holds after a restore.
   */
  stickWidth(id: BodyId): number {
    const b = this.body(id);
    const T = this.R.ShapeType;
    let w = 0;
    for (let i = 0; i < b.numColliders(); i++) {
      const c = b.collider(i);
      let dims: number[];
      switch (c.shapeType()) {
        case T.Ball:
          dims = [2 * c.radius(), 2 * c.radius(), 2 * c.radius()];
          break;
        case T.Cuboid: {
          const h = c.halfExtents()!;
          dims = [2 * h.x, 2 * h.y, 2 * h.z];
          break;
        }
        case T.Capsule:
          dims = [2 * c.radius(), 2 * c.radius(), 2 * (c.halfHeight() + c.radius())];
          break;
        case T.Cylinder:
          dims = [2 * c.radius(), 2 * c.radius(), 2 * c.halfHeight()];
          break;
        default:
          return Infinity;
      }
      dims.sort((a, b2) => a - b2);
      w = Math.max(w, dims[1]!);
    }
    return w;
  }

  /** Advance the world one fixed step. Call it once per sim step, after writing kinematic targets. */
  step(): void {
    if (this.impactColliders > 0) {
      this.events ??= new this.R.EventQueue(true);
      this.world.step(this.events);
      const out: Impact[] = [];
      this.events.drainContactForceEvents((e) => {
        const a = this.idOfCollider(this.world.getCollider(e.collider1()));
        const b = this.idOfCollider(this.world.getCollider(e.collider2()));
        if (a !== null && b !== null) out.push({ a, b, force: e.totalForceMagnitude() });
      });
      this.lastImpacts = out;
    } else {
      this.world.step();
      this.lastImpacts = [];
    }
    this.stepCount++;
  }

  /** The contacts over a collider's `impactThreshold` during the last step, in Rapier's (deterministic) order. */
  impacts(): Impact[] {
    return this.lastImpacts.map((i) => ({ ...i }));
  }

  // ── characters ───────────────────────────────────────────────────────────────────────────────────

  /** Make a kinematic body a character: it then moves with `moveCharacter`. Its first collider (a capsule or a ball)
   *  is its shape; it collides with what that collider's layer collides with. Throws for an unknown, non-kinematic or
   *  wrongly shaped body. */
  addCharacter(id: BodyId, spec: CharacterSpec): void {
    const b = this.body(id);
    if (!b.isKinematic()) throw new Error(`collision world: character "${id}" must be a kinematic body`);
    const t = b.collider(0).shapeType();
    if (t !== this.R.ShapeType.Capsule && t !== this.R.ShapeType.Ball) throw new Error(`collision world: character "${id}" needs a capsule or ball collider`);
    this.removeCharacter(id);
    this.characters.set(id, { spec: { ...spec }, ctrl: null });
  }

  /** Whether a body is a character. */
  hasCharacter(id: BodyId): boolean {
    return this.characters.has(id);
  }

  /** Stop treating a body as a character (the body stays). */
  removeCharacter(id: BodyId): void {
    const c = this.characters.get(id);
    if (!c) return;
    if (c.ctrl) this.world.removeCharacterController(c.ctrl);
    this.characters.delete(id);
  }

  /**
   * Move a character by `desired` (m) this step: Rapier works out how far it can go (sliding along walls, climbing
   * slopes up to its limit and steps up to its autostep height, snapping down to the ground) and the body's kinematic
   * target is set to its position plus that. Call once per sim step, before `step()`.
   *
   * While grounded, ask for a level move (y 0) and let the snap keep it down: a move pushing down into the ground
   * makes Rapier's controller stall now and then (measured: 22 near-zero steps in 600 on a flat box, none level).
   */
  moveCharacter(id: BodyId, desired: Vec3): CharacterMove {
    const c = this.characters.get(id);
    if (!c) throw new Error(`collision world: "${id}" is not a character`);
    const ctrl = (c.ctrl ??= this.makeController(c.spec));
    // the planted defect lives here, beside the snap it breaks
    const snap = this.faults.noGroundSnap ? null : (c.spec.snapToGround ?? null);
    if (snap !== null) ctrl.enableSnapToGround(snap);
    else ctrl.disableSnapToGround();
    const b = this.body(id);
    const col = b.collider(0);
    const flags = this.R.QueryFilterFlags.EXCLUDE_SENSORS;
    ctrl.computeColliderMovement(col, desired, flags, col.collisionGroups());
    let m: Vec3 = ctrl.computedMovement();
    // Rapier's autostep declines a step met head-on when the contact with its face comes back with a normal tilted a
    // hair downward (read as a ceiling): a step whose top is above the centre of the capsule's lower sphere, met
    // square, then stalls for good (measured: a 0.3 m-radius capsule with a 0.35 m autostep stalled in 5 to 24 of 24
    // approaches at step heights from 0.32 to 0.345 m). A grounded character stopped by a wall-like contact gets the
    // same step-up Rapier would have made (stepUp, below).
    let assisted: Vec3 | null = null;
    const ask = Math.hypot(desired.x, desired.z);
    if (c.spec.autostep && !this.faults.noStepAssist && desired.y <= 0 && ask > 1e-6 && ctrl.computedGrounded() && Math.hypot(m.x, m.z) < 0.5 * ask) {
      let wall = false;
      for (let i = 0; i < ctrl.numComputedCollisions(); i++) {
        const k = ctrl.computedCollision(i);
        if (k && Math.abs(k.normal2.y) < 0.2) wall = true;
      }
      if (wall) assisted = this.stepUp(id, col, c.spec, desired, ask);
    }
    if (assisted) m = assisted;
    const moved = { x: m.x, y: m.y, z: m.z };
    const contacts: CharacterContact[] = [];
    for (let i = 0; i < ctrl.numComputedCollisions(); i++) {
      const k = ctrl.computedCollision(i);
      if (!k?.collider) continue;
      const hid = this.idOfCollider(k.collider);
      if (hid === null || contacts.some((x) => x.id === hid)) continue;
      contacts.push({ id: hid, tag: this.tags.get(k.collider.handle) ?? null, normal: v3(k.normal2) });
    }
    const p = b.translation();
    b.setNextKinematicTranslation({ x: p.x + moved.x, y: p.y + moved.y, z: p.z + moved.z });
    return { moved, grounded: ctrl.computedGrounded(), contacts };
  }

  /** Rapier's own autostep, for a step it declined: room overhead for the step height, room on the step for its
   *  minimum width, and a walkable top there no higher than the step height over the ground; then the move rises to
   *  that top while going ahead as asked. Null when any of it fails (a wall, a step too high or too narrow, a steep top). */
  private stepUp(id: BodyId, col: RAPIER.Collider, spec: CharacterSpec, desired: Vec3, ask: number): Vec3 | null {
    const step = spec.autostep!;
    const skin = spec.skin ?? 0.02;
    const r = col.radius();
    const shape: PrimitiveShape = col.shapeType() === this.R.ShapeType.Capsule ? { type: "capsule", halfHeight: col.halfHeight(), radius: r } : { type: "ball", radius: r };
    const q = col.rotation();
    const rot = { x: q.x, y: q.y, z: q.z, w: q.w };
    const o: QueryOptions = { exclude: id, layers: this.layersSeenBy(col.collisionGroups()) };
    const p = col.translation();
    const h = step.maxHeight + skin;
    if (this.castShape(shape, p, rot, { x: 0, y: 1, z: 0 }, h, o)) return null;
    const raised = { x: p.x, y: p.y + h, z: p.z };
    const dir = { x: desired.x / ask, y: 0, z: desired.z / ask };
    const width = step.minWidth + skin;
    if (this.castShape(shape, raised, rot, dir, Math.max(width, ask) + skin, o)) return null;
    const probe = { x: raised.x + dir.x * width, y: raised.y, z: raised.z + dir.z * width };
    const down = this.castShape(shape, probe, rot, { x: 0, y: -1, z: 0 }, h, o);
    if (!down || down.normal.y < Math.cos(spec.maxClimbAngle) - 1e-6) return null;
    // the step's own height over the ground the character stands on (its base sits a skin above it)
    const base = p.y - (shape.type === "capsule" ? shape.halfHeight : 0) - r - skin;
    if (down.point.y - base > step.maxHeight + 1e-6) return null;
    const rise = h - down.distance;
    if (rise <= skin || rise > h) return null;
    return { x: desired.x, y: rise, z: desired.z };
  }

  /** What is under a character: a ball a little narrower than its shape swept down from its lowest sphere's centre, up
   *  to `maxDistance` m below its base, on the layers it collides with. `distance` is the gap from its base to the
   *  surface. Null when nothing is that close. Sees the world as of the last step; `at` probes from another body
   *  position (where a move just sent it, before the step). */
  groundUnder(id: BodyId, maxDistance: number, at?: Vec3): Hit | null {
    const b = this.body(id);
    const col = b.collider(0);
    const r = col.radius();
    const half = col.shapeType() === this.R.ShapeType.Capsule ? col.halfHeight() : 0;
    const p = at ?? col.translation();
    const probe = r * 0.9;
    const from = { x: p.x, y: p.y - half, z: p.z };
    const hit = this.castShape({ type: "ball", radius: probe }, from, { x: 0, y: 0, z: 0, w: 1 }, { x: 0, y: -1, z: 0 }, maxDistance + r - probe, {
      exclude: id,
      layers: this.layersSeenBy(col.collisionGroups()),
    });
    if (!hit) return null;
    return { ...hit, distance: Math.max(0, hit.distance - (r - probe)) };
  }

  // ── joints ───────────────────────────────────────────────────────────────────────────────────────

  /** Join two bodies (joints.ts: spherical or revolute, with limits). Throws if the ID is taken, a body is unknown or a
   *  limit is out of order. Wakes both bodies. */
  addJoint(spec: JointSpec): void {
    if (this.joints.has(spec.id)) throw new Error(`collision world: joint "${spec.id}" already exists`);
    checkJointSpec(spec);
    const a = this.body(spec.a);
    const b = this.body(spec.b);
    const j = this.world.createImpulseJoint(jointData(this.R, spec), a, b, true);
    j.setContactsEnabled(spec.contacts ?? false);
    applyLimits(this.R, j, spec);
    this.joints.set(spec.id, { handle: j.handle, spec: structuredClone(spec) });
  }

  /** Remove a joint; unknown IDs are ignored. */
  removeJoint(id: JointId): void {
    const j = this.joints.get(id);
    if (!j) return;
    this.world.removeImpulseJoint(this.world.getImpulseJoint(j.handle), true);
    this.joints.delete(id);
  }

  hasJoint(id: JointId): boolean {
    return this.joints.has(id);
  }

  /** Every joint ID, in the order added. */
  jointIds(): JointId[] {
    return [...this.joints.keys()];
  }

  /** A joint's spec as added. */
  joint(id: JointId): JointSpec {
    const j = this.joints.get(id);
    if (!j) throw new Error(`collision world: no joint "${id}"`);
    return structuredClone(j.spec);
  }

  /** A joint's angles now, read from its two bodies' rotations as Rapier's limits read them (joints.ts). */
  jointAngles(id: JointId): JointAngles {
    const j = this.joints.get(id);
    if (!j) throw new Error(`collision world: no joint "${id}"`);
    const qa = q4(this.body(j.spec.a).rotation());
    const qb = q4(this.body(j.spec.b).rotation());
    return j.spec.kind === "spherical" ? { kind: "spherical", angles: sphericalAngles(qa, qb) } : { kind: "revolute", angle: revoluteAngle(qa, qb, j.spec.axis) };
  }

  // ── water ────────────────────────────────────────────────────────────────────────────────────────

  /** Add a water volume (a box with a surface height, and an outline inside it: water.ts). Throws if its ID is taken or it is empty. */
  addWater(v: WaterVolume): void {
    this.water.add(v);
  }

  /** Remove a water volume; unknown IDs are ignored. */
  removeWater(id: string): void {
    this.water.remove(id);
  }

  /** Every water volume, in the order added. */
  waterVolumes(): WaterVolume[] {
    return this.water.all();
  }

  /** The water at a point (its surface and how deep the point is), or null on dry land or above the surface. */
  waterAt(p: Vec3): WaterAt | null {
    return this.water.at(p, { boxOnly: this.faults.waterBoxOnly });
  }

  /** The highest water surface over (x, z), or null where there is none. */
  waterSurface(x: number, z: number): number | null {
    return this.water.surfaceAt(x, z, { boxOnly: this.faults.waterBoxOnly });
  }

  // ── queries ──────────────────────────────────────────────────────────────────────────────────────

  /** The first body a ray hits within `maxDistance` metres (the direction is normalised), or null. */
  castRay(origin: Vec3, direction: Vec3, maxDistance: number, o: QueryOptions = {}): Hit | null {
    const d = normalise(direction);
    const hit = this.world.castRayAndGetNormal(new this.R.Ray(origin, d), maxDistance, true, ...this.filter(o));
    if (!hit) return null;
    const t = hit.timeOfImpact;
    return this.hitOf(hit.collider, t, { x: origin.x + d.x * t, y: origin.y + d.y * t, z: origin.z + d.z * t }, v3(hit.normal));
  }

  /** Sweep a shape from `position` along `direction` up to `maxDistance` metres; the first body it touches, or null.
   *  A shape that starts touching something hits it at distance 0. */
  castShape(shape: PrimitiveShape, position: Vec3, rotation: Quat, direction: Vec3, maxDistance: number, o: QueryOptions = {}): Hit | null {
    const d = normalise(direction);
    const hit = this.world.castShape(position, rotation, d, this.shape(shape), 0, maxDistance, true, ...this.filter(o));
    if (!hit) return null;
    return this.hitOf(hit.collider, hit.time_of_impact, v3(hit.witness1), v3(hit.normal1));
  }

  /** The bodies whose colliders overlap a shape placed at `position`, each once, in Rapier's (deterministic) order. */
  overlap(shape: PrimitiveShape, position: Vec3, rotation: Quat, o: QueryOptions = {}): BodyId[] {
    const out: BodyId[] = [];
    this.world.intersectionsWithShape(
      position,
      rotation,
      this.shape(shape),
      (c) => {
        const id = this.idOfCollider(c);
        if (id !== null && !out.includes(id)) out.push(id);
        return true;
      },
      ...this.filter(o),
    );
    return out;
  }

  /**
   * A hit volume swept along a path on the fixed step: the shape placed at `samples` points from `from` (exclusive)
   * to `to` (inclusive), each place's overlaps collected (sensors included unless `o.sensors` is false). Each body
   * once, in the order the sweep first touched it, with the place it touched it. Sweeping between the last step's
   * place and this step's catches a fast swing that a single overlap would step over (4 samples, after the combat
   * suggestion's sweep between steps).
   */
  sweep(shape: PrimitiveShape, from: Vec3, to: Vec3, rotation: Quat, o: QueryOptions = {}, samples = 4): { id: BodyId; at: Vec3 }[] {
    const out: { id: BodyId; at: Vec3 }[] = [];
    const n = Math.max(1, Math.floor(samples));
    const q: QueryOptions = { ...o, sensors: o.sensors ?? true };
    for (let i = 1; i <= n; i++) {
      const k = i / n;
      const at = { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k, z: from.z + (to.z - from.z) * k };
      for (const id of this.overlap(shape, at, rotation, q)) if (!out.some((h) => h.id === id)) out.push({ id, at });
    }
    return out;
  }

  // ── state ────────────────────────────────────────────────────────────────────────────────────────

  /** Every body's state, in ID order. */
  bodyStates(): BodyState[] {
    return [...this.byId.keys()].sort().map((id) => {
      const b = this.body(id);
      return { id, kind: kindOf(b), position: v3(b.translation()), rotation: q4(b.rotation()), linvel: v3(b.linvel()), angvel: v3(b.angvel()), sleeping: b.isSleeping() };
    });
  }

  /** A deterministic hash of every body's kind, position, rotation, velocities and sleep state (64-bit hex). */
  hash(): string {
    const h = new StateHasher();
    for (const s of this.bodyStates()) {
      h.str(s.id).str(s.kind);
      for (const v of [s.position, s.linvel, s.angvel]) h.num(v.x).num(v.y).num(v.z);
      h.num(s.rotation.x).num(s.rotation.y).num(s.rotation.z).num(s.rotation.w).value(s.sleeping);
    }
    return h.digest();
  }

  /** The whole world as JSON-safe data: Rapier's snapshot plus the ID map, tags and step count. */
  snapshot(): CollisionSnapshot {
    return {
      format: COLLISION_SNAPSHOT_FORMAT,
      rapier: toBase64(this.world.takeSnapshot()),
      bodies: [...this.byId.entries()],
      tags: [...this.tags.entries()],
      steps: this.stepCount,
      water: this.water.all(),
      joints: [...this.joints.entries()].map(([id, j]): [JointId, number, JointSpec] => [id, j.handle, structuredClone(j.spec)]),
    };
  }

  /** Replace the world with a snapshot's. The step size stays this world's `dt`. Throws on another format. */
  restore(s: CollisionSnapshot): void {
    if (!s || s.format !== COLLISION_SNAPSHOT_FORMAT) throw new Error(`collision world: not a collision snapshot (format ${String((s as { format?: unknown })?.format)})`);
    if (!this.faults.restoreWithoutSnapshot) {
      const next = this.R.World.restoreSnapshot(fromBase64(s.rapier));
      if (!next) throw new Error("collision world: Rapier could not read the snapshot");
      this.world.free();
      this.world = next;
      this.world.timestep = this.dt;
      if (this.solverIterations !== undefined) this.world.numSolverIterations = this.solverIterations;
      // Rapier's character controllers belong to the old world: rebuild them on the next move
      for (const c of this.characters.values()) c.ctrl = null;
      // the snapshot keeps each collider's groups as they were when it was taken: give every collider its layer's
      // groups from this world's table (its layer read from its membership bit), so a save from before a layer was
      // added at the table's end loads under the new table (its terrain meets the new layer too)
      this.world.forEachCollider((c) => {
        const member = c.collisionGroups() >>> 16;
        const name = this.layers.names.find((n) => this.layers.bit(n) === member);
        if (name !== undefined) c.setCollisionGroups(this.layers.groups(name));
      });
      // the colliders asking for impacts are in the snapshot's world
      this.impactColliders = 0;
      this.world.forEachCollider((c) => void (c.activeEvents() & this.R.ActiveEvents.CONTACT_FORCE_EVENTS && this.impactColliders++));
      this.lastImpacts = [];
    }
    if (s.water) this.water.set(s.water);
    this.byId = new Map(s.bodies);
    this.byHandle = new Map(s.bodies.map(([id, h]) => [h, id]));
    this.tags = new Map(s.tags);
    this.joints = new Map((s.joints ?? []).map(([id, handle, spec]) => [id, { handle, spec: structuredClone(spec) }]));
    this.stepCount = s.steps;
  }

  /** Free the Rapier world. The object is unusable after. */
  dispose(): void {
    this.world.free();
    this.byId.clear();
    this.byHandle.clear();
    this.joints.clear();
  }

  // ── internals ────────────────────────────────────────────────────────────────────────────────────

  private body(id: BodyId): RAPIER.RigidBody {
    const h = this.byId.get(id);
    if (h === undefined) throw new Error(`collision world: no body "${id}"`);
    return this.world.getRigidBody(h);
  }

  private makeController(spec: CharacterSpec): RAPIER.KinematicCharacterController {
    const k = this.world.createCharacterController(spec.skin ?? 0.02);
    k.setUp({ x: 0, y: 1, z: 0 });
    k.setMaxSlopeClimbAngle(spec.maxClimbAngle);
    k.setMinSlopeSlideAngle(spec.minSlideAngle ?? spec.maxClimbAngle);
    if (spec.autostep) k.enableAutostep(spec.autostep.maxHeight, spec.autostep.minWidth, spec.autostep.includeDynamic ?? false);
    else k.disableAutostep();
    k.setSlideEnabled(spec.slide ?? true);
    k.setApplyImpulsesToDynamicBodies(spec.pushDynamic ?? true);
    if (spec.mass !== undefined) k.setCharacterMass(spec.mass);
    return k;
  }

  /** The layer names a collider with these interaction groups collides with. */
  private layersSeenBy(groups: number): string[] {
    const filter = groups & 0xffff;
    return this.layers.names.filter((n) => (filter & this.layers.bit(n)) !== 0);
  }

  private idOfCollider(c: RAPIER.Collider): BodyId | null {
    const p = c.parent();
    return p ? (this.byHandle.get(p.handle) ?? null) : null;
  }

  private hitOf(c: RAPIER.Collider, distance: number, point: Vec3, normal: Vec3): Hit | null {
    const id = this.idOfCollider(c);
    return id === null ? null : { id, tag: this.tags.get(c.handle) ?? null, distance, point, normal };
  }

  private filter(o: QueryOptions): [number, number, undefined, RAPIER.RigidBody | undefined, ((c: RAPIER.Collider) => boolean) | undefined] {
    const flags = o.sensors ? 0 : this.R.QueryFilterFlags.EXCLUDE_SENSORS;
    const exclude = o.exclude !== undefined ? this.body(o.exclude) : undefined;
    const keep = o.filter;
    const predicate = keep
      ? (c: RAPIER.Collider): boolean => {
          const id = this.idOfCollider(c);
          return id !== null && keep(id);
        }
      : undefined;
    return [flags, this.layers.query(o.layers), undefined, exclude, predicate];
  }

  private shape(s: PrimitiveShape): RAPIER.Shape {
    const R = this.R;
    switch (s.type) {
      case "cuboid":
        return new R.Cuboid(s.halfExtents.x, s.halfExtents.y, s.halfExtents.z);
      case "ball":
        return new R.Ball(s.radius);
      case "capsule":
        return new R.Capsule(s.halfHeight, s.radius);
      case "cylinder":
        return new R.Cylinder(s.halfHeight, s.radius);
    }
  }

  private colliderDesc(s: ShapeSpec): RAPIER.ColliderDesc {
    const C = this.R.ColliderDesc;
    switch (s.type) {
      case "cuboid":
        return C.cuboid(s.halfExtents.x, s.halfExtents.y, s.halfExtents.z);
      case "ball":
        return C.ball(s.radius);
      case "capsule":
        return C.capsule(s.halfHeight, s.radius);
      case "cylinder":
        return C.cylinder(s.halfHeight, s.radius);
      case "heightfield": {
        const { rows, cols } = s;
        if (s.heights.length !== (rows + 1) * (cols + 1)) throw new Error(`heightfield: ${s.heights.length} samples, want (${rows}+1)×(${cols}+1)`);
        // ours is row-major by z; Rapier's matrix is column-major with rows along z and columns along x
        const h = new Float32Array(s.heights.length);
        for (let iz = 0; iz <= rows; iz++) for (let ix = 0; ix <= cols; ix++) h[ix * (rows + 1) + iz] = s.heights[iz * (cols + 1) + ix]!;
        return C.heightfield(rows, cols, h, s.size);
      }
      case "trimesh":
        return C.trimesh(Float32Array.from(s.vertices), Uint32Array.from(s.indices));
      case "convex": {
        const d = s.points.length >= 12 ? C.convexHull(Float32Array.from(s.points)) : null;
        if (!d || !d.shape) throw new Error(`collision world: no convex hull from ${s.points.length / 3} points`);
        return d;
      }
    }
  }
}

/** Build a collision world (await `initCollision()` first and pass what it returns). */
export function createCollisionWorld(rapier: Rapier, o: CollisionWorldOptions): CollisionWorld {
  return new CollisionWorld(rapier, o);
}

function kindOf(b: RAPIER.RigidBody): BodyKind {
  return b.isDynamic() ? "dynamic" : b.isKinematic() ? "kinematic" : "fixed";
}

const v3 = (v: Vec3): Vec3 => ({ x: v.x, y: v.y, z: v.z });
const q4 = (q: Quat): Quat => ({ x: q.x, y: q.y, z: q.z, w: q.w });

function normalise(v: Vec3): Vec3 {
  const m = Math.hypot(v.x, v.y, v.z);
  if (!(m > 0)) throw new Error("collision query: zero direction");
  return { x: v.x / m, y: v.y / m, z: v.z / m };
}
